/** Cruscotto: registers at a glance (group-toned), compliance posture from the rule tables, journeys. */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Progress, Row, Space, Statistic, Tag, Typography } from "antd";
import { ArrowRightOutlined, CompassOutlined, ExperimentOutlined } from "@ant-design/icons";
import { OrgScopeBar, getIconComponent, scopeQs, useOrgScopeStore } from "@evo-flywheel/ui";
import { descriptors } from "@kit/descriptors/index";
import { entityCount, kernel, type Evaluation, type Finding, type JourneyInfo } from "../api";
import { scopeFindings } from "../modules/obligations";
import { GROUPS, toneOfGroup } from "../nav";
import { instance } from "../instance";

const CARDS = ["cmp_processing_activity", "cmp_third_party", "cmp_asset", "cmp_threat", "cmp_measure", "cmp_data_breach", "cmp_policy", "cmp_acceptance", "cmp_audit", "cmp_worker_group", "cmp_aml_verification", "cmp_org_unit"];

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [ev, setEv] = useState<Evaluation>();
  const [journeys, setJourneys] = useState<JourneyInfo[]>([]);
  const [org, setOrg] = useState<{ label: string; synthetic: boolean }>();

  // Register counts follow the organisation scope (ADR-037); the obligations card stays group-wide.
  const { selected, withGroup } = useOrgScopeStore();
  useEffect(() => {
    Promise.all(CARDS.map(async (s) => [s, await entityCount(s, scopeQs(s))] as const)).then((r) => setCounts(Object.fromEntries(r)));
  }, [selected.join(","), withGroup]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    kernel<Evaluation>("/decisions/evaluate-stored", {}).then(setEv).catch(() => undefined);
    kernel<JourneyInfo[]>("/journeys").then(setJourneys).catch(() => undefined);
    kernel<{ entities: Array<{ label: string; metadata?: Record<string, unknown> }> }>("/entities?type=cmp_organisation_profile&limit=1")
      .then((r) => r.entities[0] && setOrg({ label: r.entities[0].label, synthetic: r.entities[0].metadata?._source === "demo-seed" })).catch(() => undefined);
  }, []);

  // obligations of the chosen companies (ADR-037, C55); the evaluation runs over the whole group
  const [shown, setShown] = useState<Finding[]>([]);
  useEffect(() => { if (ev) scopeFindings(ev.findings).then(setShown).catch(() => setShown(ev.findings)); }, [ev, selected.join(","), withGroup]); // eslint-disable-line react-hooks/exhaustive-deps
  const met = shown.filter((f) => f.status === "met").length;
  const total = shown.length;
  const blocking = shown.filter((f) => f.status === "unmet" && f.severity === "blocking").length;

  return <div>
    <Space align="baseline" style={{ marginBottom: 8 }}>
      <Typography.Title level={3} style={{ margin: 0 }}>{org?.label ?? instance.brand}</Typography.Title>
      {org?.synthetic && <Tag color="purple">dati dimostrativi</Tag>}
    </Space>
    <div><OrgScopeBar /></div>
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={8}>
        <Card title={<Space><ExperimentOutlined />Stato di conformità</Space>} extra={<Button type="link" onClick={() => navigate("/regole")}>Dettaglio</Button>} style={{ height: "100%" }}>
          <Space size={24} align="center">
            <Progress type="dashboard" percent={total ? Math.round((met / total) * 100) : 0} strokeColor={blocking ? "#d48806" : "#389e0d"} />
            <Space direction="vertical" size={0}>
              <Statistic title="Obblighi soddisfatti" value={met} suffix={`/ ${total}`} />
              <Typography.Text type={blocking ? "danger" : "success"}>{blocking} bloccanti aperti</Typography.Text>
              {selected.length > 0 && <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>società scelte{withGroup ? " ed elementi di gruppo" : ""}</Typography.Text></div>}
            </Space>
          </Space>
        </Card>
      </Col>
      <Col xs={24} lg={16}>
        <Card title={<Space><CompassOutlined />Percorsi guidati</Space>} extra={<Button type="link" onClick={() => navigate("/wizard")}>Tutti</Button>} style={{ height: "100%" }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            {journeys.map((j) => <Card.Grid key={j.journey} hoverable style={{ width: "100%", padding: 12, cursor: "pointer" }} onClick={() => navigate(`/wizard/${j.journey}`)}>
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <span>{j.title}</span><ArrowRightOutlined />
              </Space>
            </Card.Grid>)}
          </Space>
        </Card>
      </Col>
      {CARDS.filter((slug) => counts[slug] !== 0).map((slug) => {
        const d = descriptors.find((x) => x.slug === slug);
        const ui = (d?.ui ?? {}) as { icon?: string; navGroup?: string };
        const tone = toneOfGroup(ui.navGroup);
        return <Col key={slug} xs={12} md={8} xl={6}>
          <Card hoverable onClick={() => navigate(`/${slug}`)} style={{ borderTop: `3px solid ${tone}` }}>
            <Statistic title={<Space><span style={{ color: tone }}>{getIconComponent(ui.icon ?? "AppstoreOutlined")}</span>{d?.label.plural}</Space>}
              value={counts[slug] ?? "…"} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{GROUPS.find((g) => g.key === ui.navGroup)?.label}</Typography.Text>
          </Card>
        </Col>;
      })}
    </Row>
  </div>;
};
